/**
 * Plan comparison utilities — behavior separated from plan constants.
 *
 * These functions operate on the plan order array provided by the API
 * (GET /api/billing/plans → planOrder).  They are intentionally kept in a
 * utils file rather than constants/plans.js so that the constants file remains
 * pure data (PLAN_STATUS, fallback plan objects) without mixing in behavior.
 */

/**
 * Check if upgrading from one plan to another is valid.
 * Silent when order is empty/absent (plans still loading).
 * Warns when order is populated but a plan ID is missing (caller bug).
 * @param {string} fromPlanId
 * @param {string} toPlanId
 * @param {string[]} order - Plan hierarchy from API (required for dynamic plans)
 * @returns {boolean}
 */
export function isValidUpgrade(fromPlanId, toPlanId, order) {
  if (!order || order.length === 0) return false;
  const fromIndex = order.indexOf(fromPlanId);
  const toIndex = order.indexOf(toPlanId);
  if (fromIndex === -1 || toIndex === -1) {
    console.warn(
      "isValidUpgrade: unrecognised plan ID(s) in a populated order array",
      { fromPlanId, toPlanId },
    );
    return false;
  }
  return toIndex > fromIndex;
}

/**
 * Check if downgrading from one plan to another is valid.
 * Silent when order is empty/absent (plans still loading).
 * Warns when order is populated but a plan ID is missing (caller bug).
 * @param {string} fromPlanId
 * @param {string} toPlanId
 * @param {string[]} order - Plan hierarchy from API (required for dynamic plans)
 * @returns {boolean}
 */
export function isValidDowngrade(fromPlanId, toPlanId, order) {
  if (!order || order.length === 0) return false;
  const fromIndex = order.indexOf(fromPlanId);
  const toIndex = order.indexOf(toPlanId);
  if (fromIndex === -1 || toIndex === -1) {
    console.warn(
      "isValidDowngrade: unrecognised plan ID(s) in a populated order array",
      { fromPlanId, toPlanId },
    );
    return false;
  }
  return toIndex < fromIndex;
}
